<script setup lang="ts">
import { ref } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import { AppAlert, AppButton, FormInput } from 'duanaga-ui'
import { z } from 'zod'
import FetchError from '@/FetchError'
import Auth from '@/api/Auth'
import { signInWithAppleIdentity } from '@/composables/social/appleIdentity'
import { signInWithGoogleIdentity } from '@/composables/social/googleIdentity'
import useTitle from '@/composables/title'
import { useValidator } from '@/composables/validator'
import { useAuthStore } from '@/stores/auth'

useTitle('Register')

const router = useRouter()
const auth = useAuthStore()
const adminDashboardUrl = import.meta.env.VITE_ADMIN_DASHBOARD_URL?.trim()

const registering = ref(false)
const socialAuthenticating = ref<SocialProvider | null>(null)
const clientError = ref<string | null>(null)

const schema = z
  .object({
    name: z.string().min(1, 'Name is required').max(255, 'Name is too long'),
    email: z.string().email('Valid email is required'),
    password: z.string().min(8, 'Password must be at least 8 characters'),
    password_confirmation: z.string().min(8, 'Password confirmation is required')
  })
  .refine((value) => value.password === value.password_confirmation, {
    path: ['password_confirmation'],
    message: 'Password confirmation does not match'
  })

const {
  formData: form,
  formError,
  validate
} = useValidator(schema, {
  name: '',
  email: '',
  password: '',
  password_confirmation: ''
})

const completeLogin = async (response: AuthResponse): Promise<void> => {
  auth.authenticate({
    token: response.token,
    user: response.user
  })

  if (response.user.role === 'admin' && adminDashboardUrl) {
    const redirectUrl = new URL(adminDashboardUrl)
    redirectUrl.searchParams.set('token', response.token)
    redirectUrl.searchParams.set('role', response.user.role)
    redirectUrl.searchParams.set('email', response.user.email)
    redirectUrl.searchParams.set('name', response.user.name)
    window.location.assign(redirectUrl.toString())
    return
  }

  await router.push({ name: 'batches' })
}

const toMessage = (error: unknown, fallback: string): string => {
  if (error instanceof FetchError) {
    return error.message || fallback
  }

  if (error instanceof Error) {
    return error.message || fallback
  }

  return fallback
}

const attemptRegister = async (): Promise<void> => {
  if (!validate()) {
    return
  }

  registering.value = true
  clientError.value = null

  try {
    const response = await Auth.register<AuthResponse>({
      name: form.value.name,
      email: form.value.email,
      password: form.value.password,
      password_confirmation: form.value.password_confirmation,
      device_name: 'web-client'
    })

    await completeLogin(response)
  } catch (error) {
    clientError.value = toMessage(error, 'Unable to register. Please try again.')
  } finally {
    registering.value = false
  }
}

const attemptGoogleRegister = async (): Promise<void> => {
  socialAuthenticating.value = 'google'
  clientError.value = null

  try {
    const identity = await signInWithGoogleIdentity()
    const response = await Auth.authenticateSocial<SocialAuthResponse>({
      provider: 'google',
      id_token: identity.idToken,
      device_name: 'web-client'
    })

    await completeLogin(response)
  } catch (error) {
    clientError.value = toMessage(error, 'Unable to continue with Google.')
  } finally {
    socialAuthenticating.value = null
  }
}

const attemptAppleRegister = async (): Promise<void> => {
  socialAuthenticating.value = 'apple'
  clientError.value = null

  try {
    const identity = await signInWithAppleIdentity()
    const response = await Auth.authenticateSocial<SocialAuthResponse>({
      provider: 'apple',
      id_token: identity.idToken,
      nonce: identity.nonce,
      name: identity.name,
      device_name: 'web-client'
    })

    await completeLogin(response)
  } catch (error) {
    clientError.value = toMessage(error, 'Unable to continue with Apple.')
  } finally {
    socialAuthenticating.value = null
  }
}
</script>

<template>
  <section class="min-h-screen bg-grey-110">
    <div class="grid min-h-screen items-stretch overflow-hidden lg:grid-cols-[1.05fr_0.95fr]">
      <div class="flex items-center justify-center bg-white px-24 py-48 sm:px-40 lg:px-64">
        <form
          class="w-full max-w-[420px] space-y-28"
          @submit.prevent="attemptRegister"
        >
          <header class="space-y-12">
            <p class="text-12 font-600 uppercase tracking-[0.35em] text-grey-60">Code Generator</p>
            <div>
              <h1 class="text-28 font-500 text-grey-110">Create Account</h1>
              <p class="mt-8 text-16 text-grey-70">
                Set up your account to start managing batches and code lifecycle from one dashboard.
              </p>
            </div>
          </header>

          <AppAlert
            v-if="clientError"
            type="error"
            :message="clientError"
            show
            @dismiss-alert="clientError = null"
          />

          <div class="space-y-16">
            <FormInput
              v-model="form.name"
              label="Full Name"
              type="text"
              placeholder="John Doe"
              autocomplete="name"
              :error="formError.name"
              required
            />

            <FormInput
              v-model="form.email"
              label="Email"
              type="email"
              placeholder="john@example.com"
              autocomplete="email"
              :error="formError.email"
              required
            />

            <FormInput
              v-model="form.password"
              label="Password"
              type="password"
              placeholder="********"
              autocomplete="new-password"
              :error="formError.password"
              required
            />

            <FormInput
              v-model="form.password_confirmation"
              label="Confirm Password"
              type="password"
              placeholder="********"
              autocomplete="new-password"
              :error="formError.password_confirmation"
              required
            />
          </div>

          <div class="space-y-16">
            <AppButton
              type="submit"
              color="black"
              block
              :loading="registering"
              text="Create account"
            />

            <div class="grid gap-12 sm:grid-cols-2">
              <button
                type="button"
                class="rounded-12 border border-grey-40 px-16 py-12 text-14 font-500 text-grey-100 transition hover:bg-grey-10 disabled:cursor-not-allowed disabled:opacity-60"
                :disabled="Boolean(socialAuthenticating)"
                @click="attemptGoogleRegister"
              >
                {{ socialAuthenticating === 'google' ? 'Connecting...' : 'Continue with Google' }}
              </button>
              <button
                type="button"
                class="rounded-12 border border-grey-40 px-16 py-12 text-14 font-500 text-grey-100 transition hover:bg-grey-10 disabled:cursor-not-allowed disabled:opacity-60"
                :disabled="Boolean(socialAuthenticating)"
                @click="attemptAppleRegister"
              >
                {{ socialAuthenticating === 'apple' ? 'Connecting...' : 'Continue with Apple' }}
              </button>
            </div>

            <p class="text-14 text-grey-70">
              Already have an account?
              <RouterLink
                class="font-600 text-grey-110 underline decoration-grey-50 hover:decoration-grey-70"
                :to="{ name: 'login' }"
              >
                Sign in
              </RouterLink>
            </p>
          </div>
        </form>
      </div>

      <aside
        class="relative flex items-center bg-gradient-to-br from-emerald-90 via-grey-110 to-grey-110 px-32 py-48 text-white sm:px-48 lg:px-64"
      >
        <div class="absolute inset-0 opacity-50">
          <div
            class="absolute inset-y-0 left-1/4 w-[460px] rotate-12 rounded-full bg-emerald-50/20 blur-3xl"
          ></div>
          <div
            class="absolute inset-y-0 right-[-10%] w-[360px] -rotate-6 rounded-full bg-cyan-50/20 blur-3xl"
          ></div>
        </div>
        <div class="relative z-10 space-y-24">
          <p class="text-12 font-500 uppercase tracking-[0.35em] text-emerald-20">Fast onboarding</p>
          <h2 class="text-36 font-500 leading-tight">Bring your team online in minutes</h2>
          <p class="text-16 text-emerald-20">
            Register with email, Google, or Apple. Once inside, your access token is ready for
            secure API operations and dashboard workflows.
          </p>
        </div>
      </aside>
    </div>
  </section>
</template>
